import React, { useState, useRef, useEffect, useMemo } from 'react';
import './Breadcrumbs.css';

/**
 * Parses a path string into an array of breadcrumb segments.
 * 
 * Property 6: Breadcrumb Generation
 * For any valid folder path, the generated breadcrumb array shall contain all path segments
 * from root to the current folder in order, with the first element representing root.
 * 
 * @param {string} path - The current folder path (e.g., "folder1/folder2/folder3")
 * @returns {Array<{name: string, path: string}>} Array of breadcrumb segments
 */
export const parsePath = (path) => {
  // Root is always the first element
  const segments = [{ name: 'Home', path: '' }];
  
  if (!path || path.trim() === '') {
    return segments;
  }
  
  // Clean the path - remove leading/trailing slashes and normalize
  const cleanPath = path.replace(/^\/+|\/+$/g, '').trim();
  
  if (cleanPath === '') {
    return segments;
  }
  
  // Split by forward slash and filter out empty segments
  const parts = cleanPath.split('/').filter(part => part.trim() !== '');
  
  // Build cumulative paths for each segment
  let cumulativePath = '';
  for (const part of parts) {
    cumulativePath = cumulativePath ? `${cumulativePath}/${part}` : part;
    segments.push({
      name: part,
      path: cumulativePath
    });
  }
  
  return segments;
};

/**
 * Determines which segments to show and which to hide in the ellipsis menu
 * when the breadcrumb trail is too long.
 * 
 * @param {Array} segments - All breadcrumb segments
 * @param {number} maxVisible - Maximum number of visible segments (including ellipsis)
 * @returns {Object} Object with visible segments and hidden segments
 */
export const truncateSegments = (segments, maxVisible = 4) => {
  if (segments.length <= maxVisible) {
    return {
      visible: segments,
      hidden: [],
      needsTruncation: false
    };
  }
  
  // Always show: Home (first), ellipsis placeholder, and last 2 segments
  // Hidden: everything in between
  const firstSegment = segments[0]; // Home
  const lastSegments = segments.slice(-2); // Last 2 segments
  const hiddenSegments = segments.slice(1, -2); // Everything in between
  
  return {
    visible: [firstSegment, ...lastSegments],
    hidden: hiddenSegments,
    needsTruncation: true
  };
};

/**
 * Home icon SVG component
 */
const HomeIcon = () => (
  <svg 
    className="breadcrumb-home-icon" 
    viewBox="0 0 24 24" 
    fill="currentColor"
    width="16" 
    height="16"
    aria-hidden="true"
  >
    <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" />
  </svg>
);

/**
 * Chevron separator icon
 */
const ChevronIcon = () => (
  <svg 
    className="breadcrumb-separator" 
    viewBox="0 0 24 24" 
    fill="currentColor"
    width="16" 
    height="16"
    aria-hidden="true"
  >
    <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
  </svg>
);

/**
 * Ellipsis menu component for hidden breadcrumb segments
 */
const EllipsisMenu = ({ segments, onNavigate, onDrop }) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);
  const buttonRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target) &&
          buttonRef.current && !buttonRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Close menu on Escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleItemClick = (segment) => {
    onNavigate(segment.path);
    setIsOpen(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.currentTarget.classList.add('breadcrumb-drop-target');
  };

  const handleDragLeave = (e) => {
    e.currentTarget.classList.remove('breadcrumb-drop-target');
  };

  const handleDrop = (e, segment) => {
    e.preventDefault();
    e.currentTarget.classList.remove('breadcrumb-drop-target');
    
    try {
      const data = e.dataTransfer.getData('application/json');
      if (data) {
        const items = JSON.parse(data);
        onDrop(segment.path, items);
      }
    } catch (error) {
      console.error('Error handling drop:', error);
    }
    setIsOpen(false);
  };

  return (
    <div className="breadcrumb-ellipsis-container">
      <button
        ref={buttonRef}
        className="breadcrumb-ellipsis-button"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label="Show hidden folders"
      >
        •••
      </button>
      {isOpen && (
        <div 
          ref={menuRef}
          className="breadcrumb-ellipsis-menu"
          role="menu"
        >
          {segments.map((segment, index) => (
            <button
              key={segment.path || index}
              className="breadcrumb-ellipsis-item"
              onClick={() => handleItemClick(segment)}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, segment)}
              role="menuitem"
            >
              {segment.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Breadcrumbs component for file explorer navigation.
 * 
 * Displays a breadcrumb trail showing the full path from root to current folder.
 * Supports click navigation, drag-and-drop, and truncation for long paths.
 * 
 * Requirements:
 * - 4.1: Display breadcrumb trail showing full path from root to current folder
 * - 4.3: Display "Home" or home icon as root breadcrumb
 * - 4.4: Truncate middle segments with ellipsis menu when trail is too long
 * 
 * @param {Object} props
 * @param {string} props.path - Current folder path
 * @param {Function} props.onNavigate - Callback when a breadcrumb is clicked
 * @param {Function} props.onDrop - Callback when items are dropped on a breadcrumb
 * @param {number} [props.maxVisible=4] - Maximum visible segments before truncation
 */
const Breadcrumbs = ({ path, onNavigate, onDrop, maxVisible = 4 }) => {
  // Parse the path into segments
  const segments = useMemo(() => parsePath(path), [path]);
  
  // Determine truncation
  const { visible, hidden, needsTruncation } = useMemo(
    () => truncateSegments(segments, maxVisible),
    [segments, maxVisible]
  );

  const handleDragOver = (e) => {
    e.preventDefault();
    e.currentTarget.classList.add('breadcrumb-drop-target');
  };

  const handleDragLeave = (e) => {
    e.currentTarget.classList.remove('breadcrumb-drop-target');
  };

  const handleDrop = (e, segment) => {
    e.preventDefault();
    e.currentTarget.classList.remove('breadcrumb-drop-target');
    
    try {
      const data = e.dataTransfer.getData('application/json');
      if (data) {
        const items = JSON.parse(data);
        onDrop(segment.path, items);
      }
    } catch (error) {
      console.error('Error handling drop:', error);
    }
  };

  const handleClick = (segment) => {
    onNavigate(segment.path);
  };

  // Render a single breadcrumb item
  const renderBreadcrumbItem = (segment, index, isLast) => {
    const isHome = index === 0;
    
    return (
      <li key={segment.path || 'home'} className="breadcrumb-item">
        <button
          className={`breadcrumb-link ${isLast ? 'breadcrumb-current' : ''}`}
          onClick={() => handleClick(segment)}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, segment)}
          aria-current={isLast ? 'page' : undefined}
          disabled={isLast}
        >
          {isHome ? (
            <>
              <HomeIcon />
              <span className="breadcrumb-home-text">Home</span>
            </>
          ) : (
            <span className="breadcrumb-text">{segment.name}</span>
          )}
        </button>
        {!isLast && <ChevronIcon />}
      </li>
    );
  };

  return (
    <nav className="breadcrumbs" aria-label="Breadcrumb navigation">
      <ol className="breadcrumb-list">
        {needsTruncation ? (
          <>
            {/* Home segment */}
            {renderBreadcrumbItem(visible[0], 0, false)}
            
            {/* Ellipsis menu for hidden segments */}
            <li className="breadcrumb-item">
              <EllipsisMenu 
                segments={hidden} 
                onNavigate={onNavigate}
                onDrop={onDrop}
              />
              <ChevronIcon />
            </li>
            
            {/* Last visible segments */}
            {visible.slice(1).map((segment, index) => 
              renderBreadcrumbItem(
                segment, 
                index + 1, 
                index === visible.length - 2
              )
            )}
          </>
        ) : (
          segments.map((segment, index) => 
            renderBreadcrumbItem(segment, index, index === segments.length - 1)
          )
        )}
      </ol>
    </nav>
  );
};

export default Breadcrumbs;
